import type {
  DocumentReference,
  Firestore,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  Unsubscribe,
  UpdateData,
  WithFieldValue,
} from 'firebase/firestore'
import {
  Timestamp,
  collectionGroup,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  startAfter,
  where,
} from 'firebase/firestore'
import { generateShortCode } from './form_short_url_service'
import { refs } from '../models/base/firestore-refs'
import {
  ProjectEntity,
  ProjectDoc,
  projectDocConverter,
  toProjectEntity,
  toProjectSnapshotJson,
} from '../models/project/project_model'
import type { InvoiceItemDoc } from '../models/invoice/invoice-item.modl'
import { INITIAL_INVOICE_ITEM_FIELDS } from '../models/invoice/invoice-item.modl'
import { type AddTownCompanyRequest, type AddTownCompanyRequestDoc, toAddTownCompanyRequest } from '../models/project/add-town-company-request.modl'
import type { ProjectHistorySummary } from '../models/project/project-history-summary.modl'
import type { ProjectStatus } from '../utils/project-status'
import { getProjectStatusLabel } from '../utils/project-status'
import {
  canRestoreResponsePhaseFromDraftList,
  hasUnhandledUnboundDataGenRequests,
  hasUnsentAddedTownCompanies,
  resolveRestoreResponsePhaseTargetStatus,
} from '../utils/restore-response-phase'
import { getFirebaseApp } from '../config/firebase.config'
import { createBaseFields, getAll, getOne, softDelete, updateWithUpdatedAt } from './firestore-crud'
import { CompanySurveyService } from './company_survey_service'
import type { FormShortUrlDoc } from '../models/form_short_url/form-short-url.modl'
import type { PaginatedResult } from './school-account-service'
import {
  type CompanySurveyFormDoc,
  COMPANY_SURVEY_FORM_ID_CURRENT,
  toCompanySurveyFormSnapshotJson,
} from '../models/project/company_survey_form_model'
import { SchoolAccount } from '../models/account/school/school-account.modl'
import type { SchoolAccountDoc } from '../models/account/school/school-account.modl'
import { AdminAccount } from '../models/account/admin/admin-account.modl'
import { generateProjectNumberWithIndex } from '../utils/project-number'
import { MailService } from './mail-service'
import { formatLocaleDateJa } from '../utils/date-util'
/** UI フィールド名（camelCase）→ Firestore フィールド名（snake_case） */
const PROJECT_FIELD_MAPPING: Record<string, string> = {
  name: 'name',
  status: 'status',
  startDate: 'start_date',
  endDate: 'end_date',
  minExperienceDays: 'min_experience_days',
  studentGrade: 'student_grade',
  participantCount: 'participant_count',
  minParticipantsPerCompany: 'min_participants_per_company',
  maxParticipantsPerCompany: 'max_participants_per_company',
  startTime: 'start_time',
  endTime: 'end_time',
  minExperienceTime: 'min_experience_time',
  lunchNotes: 'lunch_notes',
  contactPersonName: 'contact_person_name',
  notes: 'notes',
  optionalQuestion1: 'optional_question_1',
  optionalQuestion2: 'optional_question_2',
  optionalQuestion3: 'optional_question_3',
  priorContactVisitPlan: 'prior_contact_visit_plan',
  priorContactVisitSchedule: 'prior_contact_visit_schedule',
  schoolNearestStation: 'school_nearest_station',
  preferredRouteAndTravelTime: 'preferred_route_and_travel_time',
  description: 'description',
}

/** プロジェクト作成時の入力（ProjectDoc の必須項目 + 追加質問） */
export interface CreateProjectData {
  name: string
  status?: ProjectStatus
  project_number?: string
  start_date?: Date
  end_date?: Date
  min_experience_days?: number
  student_grade?: string
  participant_count?: number
  min_participants_per_company?: number
  max_participants_per_company?: number
  start_time?: string
  end_time?: string
  min_experience_time?: number
  lunch_notes?: string | null
  contact_person_name?: string
  notes?: string | null
  optional_question_1?: string | null
  optional_question_2?: string | null
  optional_question_3?: string | null
  prior_contact_visit_plan?: string | null
  prior_contact_visit_schedule?: string | null
  school_nearest_station?: string | null
  preferred_route_and_travel_time?: string | null
  school_account?: string | null
  description?: string
}

/** 学校画面のプロジェクト作成フォーム用（camelCase） */
export interface SchoolCreateProjectInput {
  name: string
  startDate: Date
  endDate: Date
  minExperienceDays: number
  studentGrade: string
  participantCount: number
  minParticipantsPerCompany: number
  maxParticipantsPerCompany: number
  startTime: string
  endTime: string
  minExperienceTime: number
  lunchNotes?: string
  contactPersonName: string
  notes?: string
  optionalQuestion1?: string
  optionalQuestion2?: string
  optionalQuestion3?: string
  priorContactVisitPlan: string
  priorContactVisitSchedule?: string
  schoolNearestStation?: string
  preferredRouteAndTravelTime?: string
}

function toDocDate(d: Date | undefined | null): Timestamp | undefined {
  if (d == null) return undefined
  return d instanceof Date ? Timestamp.fromDate(d) : undefined
}

function resolveProjectCreatedMailTo(schoolAccountData: SchoolAccountDoc | null): string | null {
  const to = (schoolAccountData?.contact_email ?? schoolAccountData?.email ?? '').trim()
  return to || null
}

function formatProjectDateForMail(date: Date | undefined): string | undefined {
  return date ? formatLocaleDateJa(date) : undefined
}

function buildProjectDocPayload(data: CreateProjectData, status: ProjectStatus) {
  const base: Record<string, unknown> = {
    name: data.name,
    status,
  }
  if (data.project_number !== undefined) base.project_number = data.project_number
  if (data.start_date !== undefined) base.start_date = toDocDate(data.start_date)
  if (data.end_date !== undefined) base.end_date = toDocDate(data.end_date)
  if (data.min_experience_days !== undefined) base.min_experience_days = data.min_experience_days
  if (data.student_grade !== undefined) base.student_grade = data.student_grade
  if (data.participant_count !== undefined) base.participant_count = data.participant_count
  if (data.min_participants_per_company !== undefined)
    base.min_participants_per_company = data.min_participants_per_company
  if (data.max_participants_per_company !== undefined)
    base.max_participants_per_company = data.max_participants_per_company
  if (data.start_time !== undefined) base.start_time = data.start_time
  if (data.end_time !== undefined) base.end_time = data.end_time
  if (data.min_experience_time !== undefined) base.min_experience_time = data.min_experience_time
  if (data.lunch_notes !== undefined) base.lunch_notes = data.lunch_notes
  if (data.contact_person_name !== undefined) base.contact_person_name = data.contact_person_name
  if (data.notes !== undefined) base.notes = data.notes
  if (data.optional_question_1 !== undefined) base.optional_question_1 = data.optional_question_1
  if (data.optional_question_2 !== undefined) base.optional_question_2 = data.optional_question_2
  if (data.optional_question_3 !== undefined) base.optional_question_3 = data.optional_question_3
  if (data.prior_contact_visit_plan !== undefined) base.prior_contact_visit_plan = data.prior_contact_visit_plan
  if (data.prior_contact_visit_schedule !== undefined) base.prior_contact_visit_schedule = data.prior_contact_visit_schedule
  if (data.school_nearest_station !== undefined) base.school_nearest_station = data.school_nearest_station
  if (data.preferred_route_and_travel_time !== undefined) {
    base.preferred_route_and_travel_time = data.preferred_route_and_travel_time
  }
  if (data.school_account !== undefined) base.school_account = data.school_account
  if (data.description !== undefined) base.description = data.description
  return base
}

/** プロジェクト履歴に追加する際の更新者情報 */
export type ProjectHistoryUpdatedBy = {
  userType: 'admin' | 'school'
  userId: string
  userName: string
  userPath: string
}

/** プロジェクト履歴1件分の書き込みペイロードを組み立てる（共通） */
function buildProjectHistoryPayload(
  updatedProjectEntity: ProjectEntity,
  updatedBy: ProjectHistoryUpdatedBy,
  updateInfo: string,
  timestamp: Timestamp,
  formSnapshot?: Record<string, unknown> | null,
): {
  project: Record<string, unknown>
  form?: Record<string, unknown>
  updated_by_user_type: 'admin' | 'school'
  updated_by_user_id: string
  updated_by_name: string
  updated_by_user_document_path: string
  update_info: string
  created_at: Timestamp
  updated_at: Timestamp
  is_deleted: boolean
} {
  const payload = {
    project: toProjectSnapshotJson(updatedProjectEntity),
    updated_by_user_type: updatedBy.userType,
    updated_by_user_id: updatedBy.userId,
    updated_by_name: updatedBy.userName,
    updated_by_user_document_path: updatedBy.userPath,
    update_info: updateInfo,
    created_at: timestamp,
    updated_at: timestamp,
    is_deleted: false,
  }
  if (formSnapshot != null) {
    return { ...payload, form: formSnapshot }
  }
  return payload
}

export class ProjectService {

  async getAllProjects(schoolId: string, includeDeleted: boolean = false): Promise<ProjectEntity[]> {
    try {
      return await getAll(refs.projects(schoolId), toProjectEntity, { includeDeleted })
    } catch (error) {
      console.error('Error getting projects:', error);
      throw error;
    }
  }

  /**
   * 学校アカウント配下のプロジェクトをページング取得（admin 学校詳細の案件一覧で利用）
   * - 並び順: created_at desc（新しい順）
   * - includeDeleted=false のとき is_deleted==false のみ
   */
  async getPaginatedForSchoolAccount(
    schoolAccountId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<ProjectDoc> | null,
    includeDeleted: boolean = false,
  ): Promise<PaginatedResult<ProjectEntity> | null> {
    const baseConstraints: QueryConstraint[] = []
    if (!includeDeleted) baseConstraints.push(where('is_deleted', '==', false))
    baseConstraints.push(orderBy('created_at', 'desc'))
    baseConstraints.push(limit(pageSize + 1))
    if (lastDocument) baseConstraints.push(startAfter(lastDocument))

    const q = query(refs.projects(schoolAccountId), ...baseConstraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs

    const hasNextPage = docs.length > pageSize
    const dataDocs = hasNextPage ? docs.slice(0, pageSize) : docs
    const lastDoc = dataDocs.length > 0 ? (dataDocs[dataDocs.length - 1] as QueryDocumentSnapshot<ProjectDoc>) : null

    const data = dataDocs.map((d) => {
      const entity = toProjectEntity(d.id, d.data() as ProjectDoc, d.ref.path)
      entity.school_account_id = schoolAccountId
      return entity
    })

    return { data, lastDoc, hasNextPage }
  }

  async getProject(
    schoolId: string,
    projectId: string,
    includeDeleted: boolean = false,
  ): Promise<ProjectEntity | null> {
    try {
      return await getOne(refs.project(schoolId, projectId), toProjectEntity, { includeDeleted })
    } catch (error) {
      console.error('Error getting project:', error);
      throw error;
    }
  }

  /**
   * 学校アカウント配下のプロジェクトを1件取得（admin 等で利用）
   * @param schoolAccountId - 学校アカウントID
   * @param projectId - プロジェクトID
   * @returns 取得した ProjectEntity（school_account_id をセット）、存在しなければ null
   */
  async getProjectBySchoolAccount(
    schoolAccountId: string,
    projectId: string,
    includeDeleted: boolean = false,
  ): Promise<ProjectEntity | null> {
    try {
      const entity = await getOne(
        refs.schoolAccountProject(schoolAccountId, projectId),
        toProjectEntity,
        { includeDeleted },
      )
      if (entity) {
        entity.school_account_id = schoolAccountId
      }
      return entity
    } catch (error) {
      console.error('Error getting project by school account:', error)
      throw error
    }
  }

  /**
   * 学校アカウント配下のプロジェクト1件をリアルタイム購読
   */
  subscribeProjectBySchoolAccount(
    schoolAccountId: string,
    projectId: string,
    onNext: (project: ProjectEntity | null) => void,
    onError?: (error: Error) => void,
    includeDeleted: boolean = false,
  ): Unsubscribe {
    const projectRef = refs.schoolAccountProject(schoolAccountId, projectId)
    return onSnapshot(
      projectRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          onNext(null)
          return
        }
        const docData = snapshot.data() as ProjectDoc
        if (!includeDeleted && docData.is_deleted === true) {
          onNext(null)
          return
        }
        const entity = toProjectEntity(snapshot.id, docData, snapshot.ref.path)
        entity.school_account_id = schoolAccountId
        onNext(entity)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error(String(err))
        if (onError) onError(e)
      },
    )
  }

  /**
   * 管理者がプロジェクトの1フィールドを更新し、履歴を作成する（admin 用）
   */
  async updateProjectFieldAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    field: string,
    value: unknown,
    adminUser: { id: string; name: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.updateProjectFieldWithHistory(schoolAccountId, projectId, currentProject, field, value, updateInfo, {
      userType: 'admin',
      userId: adminUser.id,
      userName: adminUser.name,
      userPath: adminUser.path,
    })
  }

  /**
   * 事業所データ用の6桁管理IDを生成（重複チェック付き）
   * @param schoolAccountId - 学校アカウントID
   * @param projectId - プロジェクトID
   * @returns 重複のない6桁の管理ID
   */
  async generateManagementId(schoolAccountId: string, projectId: string): Promise<string> {
    const townCompaniesRef = refs.schoolAccountTownCompanies(schoolAccountId, projectId)
    const maxAttempts = 100
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const randomId = Math.floor(Math.random() * 900000) + 100000
      const managementId = String(randomId).padStart(6, '0')
      const q = query(
        townCompaniesRef,
        where('management_id', '==', managementId),
      )
      const snapshot = await getDocs(q)
      if (snapshot.empty) return managementId
    }
    throw new Error('管理IDの生成に失敗しました。しばらく時間をおいて再度お試しください。')
  }

  /**
   * 対応中（in_progress）のプロジェクトリクエストの DocumentReference を1件取得（admin 用・郵送先登録時に紐づけ用）
   * @param schoolAccountId - 学校アカウントID
   * @param projectId - プロジェクトID
   * @returns 対応中のリクエストの参照。無い場合は null
   */
  async getInProgressRequestRef(
    schoolAccountId: string,
    projectId: string,
  ): Promise<DocumentReference<AddTownCompanyRequestDoc, AddTownCompanyRequestDoc> | null> {
    const requestsRef = refs.addTownCompanyRequests(schoolAccountId, projectId)
    const q = query(
      requestsRef,
      where('is_deleted', '==', false),
      where('status', '==', 'in_progress'),
    )
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null
    const sorted = [...snapshot.docs].sort((a, b) => {
      const aTime = a.data().created_at?.toMillis?.() ?? 0
      const bTime = b.data().created_at?.toMillis?.() ?? 0
      return bTime - aTime
    })
    const first = sorted[0]
    return refs.addTownCompanyRequest(schoolAccountId, projectId, first.id)
  }

  /**
   * プロジェクトを作成（プロジェクト・フォーム・請求明細・短縮URLを同一トランザクションで作成）
   * @param data - プロジェクトのデータ
   * @returns 作成されたプロジェクトのID
   */
  async createProject(schoolId: string, data: CreateProjectData): Promise<string> {
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const formatYMD = (d: Date | undefined) =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null
    const projectPayload = buildProjectDocPayload(data, data.status ?? 'draft_info') as Record<string, unknown>
    const formPayload: Record<string, unknown> = {
      title: data.name ?? '',
      description: data.description ?? '',
      optional_question_1: data.optional_question_1 ?? null,
      optional_question_2: data.optional_question_2 ?? null,
      optional_question_3: data.optional_question_3 ?? null,
      project_number: data.project_number ?? null,
      project_name: data.name ?? null,
      project_start_date: formatYMD(data.start_date) ?? null,
      project_end_date: formatYMD(data.end_date) ?? null,
      project_description: data.description ?? null,
      participant_count: data.participant_count ?? null,
      created_at: timestamp,
      updated_at: timestamp,
      is_deleted: false,
    }
    const shortCodeCollisionError = new Error('FORM_SHORT_CODE_COLLISION')
    const maxAttempts = 15
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const code = generateShortCode()
      try {
        const projectId = await runTransaction(db, async (transaction) => {
          const shortUrlRef = refs.formShortUrl(code)
          const shortUrlSnap = await transaction.get(shortUrlRef)
          if (shortUrlSnap.exists()) throw shortCodeCollisionError
          const schoolAccountSnap = await transaction.get(refs.schoolAccount(schoolId))
          const schoolAccountData = schoolAccountSnap.exists()
            ? (schoolAccountSnap.data() as SchoolAccountDoc)
            : null
          const isTestSchool =
            schoolAccountSnap.exists() &&
            schoolAccountData?.is_test_school === true
          const projectCollectionRef = refs.projects(schoolId)
          const newProjectRef = doc(projectCollectionRef)
          const projectId = newProjectRef.id
          transaction.set(newProjectRef, {
            ...projectPayload,
            form_short_code: code,
            created_at: timestamp,
            updated_at: timestamp,
            is_deleted: false,
          } as WithFieldValue<ProjectDoc>)
          transaction.set(refs.companySurveyForm(schoolId, projectId), formPayload as CompanySurveyFormDoc)
          if (!isTestSchool) {
            const invoicePayload = {
              ...INITIAL_INVOICE_ITEM_FIELDS,
              created_at: timestamp,
              updated_at: timestamp,
              is_deleted: false,
            }
            transaction.set(
              doc(refs.schoolAccountInvoiceItems(schoolId, projectId)),
              invoicePayload as WithFieldValue<InvoiceItemDoc>,
            )
          }
          transaction.set(shortUrlRef, {
            school_account_id: schoolId,
            project_id: projectId,
          } as FormShortUrlDoc)

          const mailTo = resolveProjectCreatedMailTo(schoolAccountData)
          if (mailTo) {
            const projectCreatedMailPayload = MailService.projectCreated({
              to: mailTo,
              schoolName: schoolAccountData?.school_name,
              contactName: schoolAccountData?.contact_name,
              projectName: data.name,
              startDateText: formatProjectDateForMail(data.start_date),
              endDateText: formatProjectDateForMail(data.end_date),
              studentGrade: data.student_grade,
              projectContactPersonName: data.contact_person_name,
              registeredAtText: formatLocaleDateJa(now),
              memo: `project_created: ${projectId}`,
            })
            transaction.set(doc(refs.mail()), projectCreatedMailPayload)
          }

          return projectId
        })
        return projectId
      } catch (err) {
        if (err === shortCodeCollisionError && attempt < maxAttempts - 1) continue
        console.error('Error creating project:', err)
        throw err
      }
    }
    throw new Error('短縮コードの生成に失敗しました。しばらく経ってからお試しください。')
  }

  /**
   * 学校がプロジェクトを作成する（案件番号カウンター更新＋プロジェクト作成をトランザクションで実行）
   * @param schoolAccountId - 学校アカウントID
   * @param schoolAccountPath - 学校アカウントの Firestore パス（school_account フィールド用）
   * @param data - フォーム入力（camelCase）
   * @returns 作成されたプロジェクトID と 案件番号
   */
  async createProjectAsSchool(
    schoolAccountId: string,
    schoolAccountPath: string,
    data: SchoolCreateProjectInput,
  ): Promise<{ projectId: string; projectNumber: string }> {
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const projectCollectionRef = refs.schoolAccountProjects(schoolAccountId)
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const seedRef = refs.globalProjectNumberSeed(dateStr)
    const candidateCodes = Array.from({ length: 5 }, () => generateShortCode())
    const result = await runTransaction(db, async (transaction) => {
      const schoolAccountRef = refs.schoolAccount(schoolAccountId)
      const schoolAccountSnap = await transaction.get(schoolAccountRef)
      const schoolAccountData = schoolAccountSnap.exists()
        ? (schoolAccountSnap.data() as SchoolAccountDoc)
        : null
      const schoolAddress = schoolAccountData
        ? `${schoolAccountData.prefecture ?? ''}${schoolAccountData.city ?? ''}${schoolAccountData.address ?? ''}`.trim()
        : ''

      const seedSnap = await transaction.get(seedRef)
      const lastIndex = (seedSnap.exists() ? (seedSnap.data() as { last_index?: number }).last_index : undefined) ?? 0
      let chosenCode: string | null = null
      for (const code of candidateCodes) {
        const shortUrlSnap = await transaction.get(refs.formShortUrl(code))
        if (!shortUrlSnap.exists()) {
          chosenCode = code
          break
        }
      }
      if (!chosenCode) {
        throw new Error('短縮コードの生成に失敗しました。しばらく経ってからお試しください。')
      }
      const projectNumber = generateProjectNumberWithIndex(now, lastIndex)
      transaction.set(seedRef, { last_index: lastIndex + 1 })
      const newProjectRef = doc(projectCollectionRef)

      const projectData: WithFieldValue<ProjectDoc> = {
        project_number: projectNumber,
        name: data.name.trim(),
        status: 'draft_info',
        form_short_code: chosenCode,
        start_date: Timestamp.fromDate(data.startDate),
        end_date: Timestamp.fromDate(data.endDate),
        min_experience_days: data.minExperienceDays,
        student_grade: data.studentGrade.trim(),
        participant_count: data.participantCount,
        min_participants_per_company: data.minParticipantsPerCompany,
        max_participants_per_company: data.maxParticipantsPerCompany,
        start_time: data.startTime,
        end_time: data.endTime,
        min_experience_time: data.minExperienceTime,
        lunch_notes: data.lunchNotes?.trim() ?? null,
        contact_person_name: data.contactPersonName.trim(),
        notes: data.notes?.trim() ?? null,
        optional_question_1: data.optionalQuestion1?.trim() || null,
        optional_question_2: data.optionalQuestion2?.trim() || null,
        optional_question_3: data.optionalQuestion3?.trim() || null,
        prior_contact_visit_plan: data.priorContactVisitPlan.trim(),
        prior_contact_visit_schedule: data.priorContactVisitSchedule?.trim() || null,
        school_nearest_station: data.schoolNearestStation?.trim() || null,
        preferred_route_and_travel_time: data.preferredRouteAndTravelTime?.trim() || null,
        school_account: schoolAccountPath,
        created_at: timestamp,
        updated_at: timestamp,
        is_deleted: false,
      }
      transaction.set(newProjectRef, projectData)

      // 利用明細（システム利用料）を Project 配下に作成（テスト学校は請求データを作らない）
      if (schoolAccountData?.is_test_school !== true) {
        const invoiceItemRef = doc(refs.schoolAccountInvoiceItems(schoolAccountId, newProjectRef.id))
        transaction.set(
          invoiceItemRef,
          {
            ...INITIAL_INVOICE_ITEM_FIELDS,
            created_at: timestamp,
            updated_at: timestamp,
            is_deleted: false,
          } as WithFieldValue<InvoiceItemDoc>,
        )
      }

      const formatYMD = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const formRef = refs.companySurveyForm(schoolAccountId, newProjectRef.id)
      const formPayload: WithFieldValue<CompanySurveyFormDoc> = {
        title: data.name.trim(),
        description: '',
        optional_question_1: data.optionalQuestion1?.trim() || null,
        optional_question_2: data.optionalQuestion2?.trim() || null,
        optional_question_3: data.optionalQuestion3?.trim() || null,
        project_number: projectNumber,
        project_name: data.name.trim(),
        project_start_date: formatYMD(data.startDate),
        project_end_date: formatYMD(data.endDate),
        project_description: null,
        project_start_time: data.startTime ?? null,
        project_end_time: data.endTime ?? null,
        project_min_experience_days: data.minExperienceDays ?? null,
        project_min_experience_time: data.minExperienceTime ?? null,
        project_student_grade: data.studentGrade.trim() || null,
        participant_count: data.participantCount ?? null,
        project_min_participants_per_company: data.minParticipantsPerCompany ?? null,
        project_max_participants_per_company: data.maxParticipantsPerCompany ?? null,
        project_contact_person_name: data.contactPersonName.trim() || null,
        project_lunch_notes: data.lunchNotes?.trim() ?? null,
        project_prior_contact_visit_plan: data.priorContactVisitPlan.trim() || null,
        project_prior_contact_visit_schedule: data.priorContactVisitSchedule?.trim() || null,
        project_school_nearest_station: data.schoolNearestStation?.trim() || null,
        project_preferred_route_and_travel_time: data.preferredRouteAndTravelTime?.trim() || null,
        school_name: schoolAccountData?.school_name ?? null,
        school_principal: schoolAccountData?.principal ?? null,
        school_postal_code: schoolAccountData?.postal_code?.trim() || null,
        school_address: schoolAddress || null,
        school_phone: schoolAccountData?.phone ?? null,
        school_email: schoolAccountData?.contact_email ?? schoolAccountData?.email ?? null,
        ...createBaseFields(false),
      } as WithFieldValue<CompanySurveyFormDoc>
      transaction.set(formRef, formPayload)

      transaction.set(refs.formShortUrl(chosenCode), {
        school_account_id: schoolAccountId,
        project_id: newProjectRef.id,
      } as FormShortUrlDoc)
      transaction.update(newProjectRef, { updated_at: timestamp })

      const mailTo = resolveProjectCreatedMailTo(schoolAccountData)
      if (mailTo) {
        const projectCreatedMailPayload = MailService.projectCreated({
          to: mailTo,
          schoolName: schoolAccountData?.school_name,
          contactName: schoolAccountData?.contact_name,
          projectName: data.name,
          startDateText: formatProjectDateForMail(data.startDate),
          endDateText: formatProjectDateForMail(data.endDate),
          studentGrade: data.studentGrade,
          projectContactPersonName: data.contactPersonName,
          registeredAtText: formatLocaleDateJa(now),
          memo: `project_created: ${newProjectRef.id}`,
        })
        transaction.set(doc(refs.mail()), projectCreatedMailPayload)
      }

      return { projectId: newProjectRef.id, projectNumber }
    })
    return result
  }

  /**
   * プロジェクトを削除（論理削除）
   * @param projectId - プロジェクトID
   */
  async deleteProject(schoolId: string, projectId: string): Promise<void> {
    try {
      await softDelete<ProjectDoc>(refs.project(schoolId, projectId))
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  /**
   * 事業所追加依頼を完了にし、進行状況を「データ承認待ち」に更新する（admin 用）
   */
  async completeAddTownCompanyRequestAsAdmin(
    schoolAccountId: string,
    projectId: string,
    requestId: string,
    adminAccount: AdminAccount,
  ): Promise<ProjectEntity | null> {
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const requestRef = refs.addTownCompanyRequest(schoolAccountId, projectId, requestId)
    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const townCompaniesCollectionRef = refs.schoolAccountTownCompanies(schoolAccountId, projectId)

    const townCompaniesQuery = query(
      townCompaniesCollectionRef,
      where('add_town_company_request_id', '==', requestId),
      where('is_deleted', '==', false),
    )
    const townCompaniesSnapshot = await getDocs(townCompaniesQuery)
    const townCompanyRefs = townCompaniesSnapshot.docs.map((d) => d.ref)

    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      const requestDocSnap = await transaction.get(requestRef)

      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }
      if (!requestDocSnap.exists()) {
        throw new Error('リクエストデータが見つかりません')
      }
      const requestStatus = (requestDocSnap.data() as { status?: string | null }).status
      if (requestStatus !== 'in_progress' && requestStatus !== '対応中') {
        throw new Error('対応中のリクエストのみキャンセルできます')
      }

      const currentProject = toProjectEntity(
        projectDocSnap.id,
        projectDocSnap.data() as ProjectDoc,
        projectDocSnap.ref.path,
      )

      const completedRequestUpdate: Partial<AddTownCompanyRequestDoc> = {
        status: 'completed' as const,
        completed_at: timestamp,
        completed_by_user_type: 'admin' as const,
        completed_by_user_id: adminAccount.id,
        completed_by_name: adminAccount.nameKanji || '管理者',
        completed_by_user_document_path: adminAccount.path,
        updated_at: timestamp,
        ...(townCompanyRefs.length > 0
          ? { town_company_references: townCompanyRefs.map((ref) => ref.path) }
          : {}),
      }

      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'waiting_for_approval',
        updated_at: now,
      }

      const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'admin',
          userId: adminAccount.id,
          userName: adminAccount.nameKanji || '管理者',
          userPath: adminAccount.path,
        },
        'データ生成を完了し、進行状況を「データ確認中」に更新',
        timestamp,
      )

      transaction.update(requestRef, completedRequestUpdate)
      transaction.update(projectDocRef, {
        status: 'waiting_for_approval',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    const projectData = await this.getProjectBySchoolAccount(schoolAccountId, projectId)
    return projectData
  }

  /**
   * 事業所追加依頼をキャンセルにし、進行状況を「データ承認待ち」に更新する（admin 用）
   */
  async cancelAddTownCompanyRequestAsAdmin(
    schoolAccountId: string,
    projectId: string,
    requestId: string,
    adminAccount: AdminAccount,
  ): Promise<ProjectEntity | null> {
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const requestRef = refs.addTownCompanyRequest(schoolAccountId, projectId, requestId)
    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)

    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      const requestDocSnap = await transaction.get(requestRef)

      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }
      if (!requestDocSnap.exists()) {
        throw new Error('リクエストデータが見つかりません')
      }

      const currentProject = toProjectEntity(
        projectDocSnap.id,
        projectDocSnap.data() as ProjectDoc,
        projectDocSnap.ref.path,
      )

      const cancelledRequestUpdate: Partial<AddTownCompanyRequestDoc> = {
        status: 'cancelled' as const,
        completed_at: timestamp,
        completed_by_user_type: 'admin' as const,
        completed_by_user_id: adminAccount.id,
        completed_by_name: adminAccount.nameKanji || '管理者',
        completed_by_user_document_path: adminAccount.path,
        updated_at: timestamp,
      }

      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'waiting_for_approval',
        updated_at: now,
      }

      const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'admin',
          userId: adminAccount.id,
          userName: adminAccount.nameKanji || '管理者',
          userPath: adminAccount.path,
        },
        'リクエストをキャンセルし、進行状況を「データ確認中」に更新',
        timestamp,
      )

      transaction.update(requestRef, cancelledRequestUpdate)
      transaction.update(projectDocRef, {
        status: 'waiting_for_approval',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    const projectData = await this.getProjectBySchoolAccount(schoolAccountId, projectId)
    return projectData
  }

  /**
   * プロジェクト履歴概要を取得（school / admin 共通）
   * @param maxCount - 取得上限件数（デフォルト: 30。一覧のページングは getProjectHistoriesPage を利用）
   */
  async getProjectHistories(
    schoolAccountId: string,
    projectId: string,
    maxCount: number = 30,
  ): Promise<ProjectHistorySummary[]> {
    const page = await this.getProjectHistoriesPage(schoolAccountId, projectId, maxCount)
    return page.items
  }

  /**
   * プロジェクト履歴概要をページング取得（school / admin 共通）
   * @param pageSize - 1ページあたり件数
   * @param lastCreatedAt - 次ページ取得時のカーソル（前ページ最後の created_at）
   */
  async getProjectHistoriesPage(
    schoolAccountId: string,
    projectId: string,
    pageSize: number,
    lastCreatedAt?: Date,
  ): Promise<{ items: ProjectHistorySummary[]; hasMore: boolean; nextCursor?: Date }> {
    const historyRef = refs.projectHistories(schoolAccountId, projectId)
    const constraints: QueryConstraint[] = [orderBy('created_at', 'desc')]
    if (lastCreatedAt) {
      constraints.push(startAfter(Timestamp.fromDate(lastCreatedAt)))
    }
    constraints.push(limit(pageSize + 1))
    const q = query(historyRef, ...constraints)
    const snapshot = await getDocs(q)
    const docs = snapshot.docs
    const hasMore = docs.length > pageSize
    const pageDocs = hasMore ? docs.slice(0, pageSize) : docs
    const items = pageDocs.map((d) => {
      const data = d.data() as {
        created_at?: { toDate?: () => Date }
        update_info?: string
        updated_by_user_type?: string
      }
      const createdAt = data?.created_at?.toDate ? data.created_at.toDate() : new Date()
      return {
        summary: data?.update_info || 'プロジェクト情報が更新されました',
        updatedByUserType: (data?.updated_by_user_type || 'school') as 'school' | 'admin',
        created_at: createdAt,
        documentId: d.id,
      }
    })
    const nextCursor = items.length > 0 ? items[items.length - 1].created_at : undefined
    return { items, hasMore, nextCursor }
  }

  /**
   * 事業所追加依頼を購読（school / admin 共通）
   */
  subscribeAddTownCompanyRequests(
    schoolAccountId: string,
    projectId: string,
    onNext: (requests: Array<{
      id: string
      date: string
      requestedCount: number
      status: string
      completedAt?: string
      isBilled: boolean
      sendRequestNumber?: string
    }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const requestsRef = refs.addTownCompanyRequests(schoolAccountId, projectId)
    const q = query(
      requestsRef,
      where('is_deleted', '==', false),
      orderBy('created_at', 'desc'),
    )
    return onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((d) => {
          const pr = toAddTownCompanyRequest(d.id, d.data() as AddTownCompanyRequestDoc, d.ref.path)
          const statusLabel =
            pr.status === 'pending'
              ? '未対応'
              : pr.status === 'in_progress'
                ? '対応中'
                : pr.status === 'completed'
                  ? '完了'
                  : 'キャンセル'
          return {
            id: pr.id,
            date: pr.created_at.toLocaleString('ja-JP'),
            requestedCount: pr.requestedCount,
            status: statusLabel,
            completedAt: pr.completedAt ? pr.completedAt.toLocaleString('ja-JP') : undefined,
            isBilled: pr.isBilled,
            sendRequestNumber: pr.sendRequestNumber,
          }
        })
        onNext(requests)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error(String(err))
        if (onError) onError(e)
      },
    )
  }

  /**
   * 事前連絡・訪問（実施予定＋実施予定日）を1回の更新で保存し、フォームへ同期する（school 用）
   */
  async updatePriorContactVisitAsSchool(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    next: { plan: string; schedule?: string },
    schoolUser: { id: string; schoolName: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.updatePriorContactVisitWithHistory(
      schoolAccountId,
      projectId,
      currentProject,
      next,
      updateInfo,
      {
        userType: 'school',
        userId: schoolUser.id,
        userName: schoolUser.schoolName,
        userPath: schoolUser.path,
      },
    )
  }

  /**
   * 事前連絡・訪問（実施予定＋実施予定日）を1回の更新で保存し、フォームへ同期する（admin 用）
   */
  async updatePriorContactVisitAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    next: { plan: string; schedule?: string },
    adminUser: { id: string; name: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.updatePriorContactVisitWithHistory(
      schoolAccountId,
      projectId,
      currentProject,
      next,
      updateInfo,
      {
        userType: 'admin',
        userId: adminUser.id,
        userName: adminUser.name,
        userPath: adminUser.path,
      },
    )
  }

  /**
   * 学校がプロジェクトの1フィールドを更新し、履歴を作成する（school 用）
   */
  async updateProjectFieldAsSchool(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    field: string,
    value: unknown,
    schoolUser: { id: string; schoolName: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.updateProjectFieldWithHistory(schoolAccountId, projectId, currentProject, field, value, updateInfo, {
      userType: 'school',
      userId: schoolUser.id,
      userName: schoolUser.schoolName,
      userPath: schoolUser.path,
    })
  }

  /**
   * 事前連絡・訪問（実施予定＋実施予定日）を1回の更新で保存し、履歴を作成する（内部共通処理）
   */
  private async updatePriorContactVisitWithHistory(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    next: { plan: string; schedule?: string },
    updateInfo: string,
    updatedBy: {
      userType: 'admin' | 'school'
      userId: string
      userName: string
      userPath: string
    },
  ): Promise<ProjectEntity> {
    const nextSchedule =
      next.plan === 'will_do' ? (next.schedule?.trim() || null) : null

    const projectUpdate: UpdateData<ProjectDoc> = {
      prior_contact_visit_plan: next.plan,
      prior_contact_visit_schedule:
        next.plan === 'will_do' ? (next.schedule?.trim() || null) : deleteField(),
    }

    return this.commitProjectUpdateWithFormSync(
      schoolAccountId,
      projectId,
      currentProject,
      projectUpdate,
      (base, now) => ({
        ...base,
        prior_contact_visit_plan: next.plan,
        prior_contact_visit_schedule: nextSchedule,
        updated_at: now,
      }),
      updatedBy,
      updateInfo,
    )
  }

  /**
   * プロジェクトの1フィールドを更新し、履歴を作成する（内部共通処理）
   */
  private async updateProjectFieldWithHistory(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    field: string,
    value: unknown,
    updateInfo: string,
    updatedBy: {
      userType: 'admin' | 'school'
      userId: string
      userName: string
      userPath: string
    },
  ): Promise<ProjectEntity> {
    const firestoreField = PROJECT_FIELD_MAPPING[field]
    if (!firestoreField) {
      throw new Error(`フィールド ${field} のマッピングが見つかりません`)
    }

    const isClearOptional = (value === undefined || value === null) &&
      [
        'optional_question_1',
        'optional_question_2',
        'optional_question_3',
        'notes',
        'lunch_notes',
        'prior_contact_visit_plan',
        'prior_contact_visit_schedule',
        'school_nearest_station',
        'preferred_route_and_travel_time',
      ].includes(firestoreField)

    let firestoreValue: unknown = value
    if (isClearOptional) {
      firestoreValue = deleteField()
    } else if (value instanceof Date) {
      firestoreValue = Timestamp.fromDate(value)
    }

    return this.commitProjectUpdateWithFormSync(
      schoolAccountId,
      projectId,
      currentProject,
      { [firestoreField]: firestoreValue } as UpdateData<ProjectDoc>,
      (base, now) => ({
        ...base,
        [firestoreField]: isClearOptional ? null : value,
        updated_at: now,
      }),
      updatedBy,
      updateInfo,
    )
  }

  /**
   * プロジェクトの更新・履歴作成・事業所回答フォームへの同期を「1つのトランザクション」で行う共通処理。
   *
   * フォーム同期をトランザクション外で後追いしていた従来実装では、
   * - コミット直後の getDoc がキャッシュ都合で更新前の値を返し、編集したフィールドが旧値で同期される
   * - 同期が失敗するとプロジェクトだけ更新されフォームが取り残される（非アトミック）
   * といったズレが発生していた。ここではトランザクション内で最新のプロジェクトを読み、
   * 編集を適用したエンティティからプロジェクト・履歴・フォームをまとめて書き込むことで原子性と整合性を担保する。
   *
   * @param projectUpdate プロジェクトドキュメントへ書き込む部分更新（updated_at は内部で付与）
   * @param applyEdit     読み取った最新プロジェクトへ編集を適用し、履歴・フォーム同期に使うエンティティを返す
   */
  private async commitProjectUpdateWithFormSync(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    projectUpdate: UpdateData<ProjectDoc>,
    applyEdit: (base: ProjectEntity, now: Date) => ProjectEntity,
    updatedBy: ProjectHistoryUpdatedBy,
    updateInfo: string,
  ): Promise<ProjectEntity> {
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const newHistoryRef = doc(refs.projectHistories(schoolAccountId, projectId))
    const formRef = refs.companySurveyForm(schoolAccountId, projectId)

    const surveyService = new CompanySurveyService()
    // 学校情報（school_*）の読み取りはトランザクション外で行う（プロジェクト編集とは独立で、頻繁には変わらないため）。
    const schoolFormInfo = await surveyService.getSchoolDisplayInfoForForm(schoolAccountId)

    const db = getFirestore(getFirebaseApp())
    let entityForReturn: ProjectEntity = applyEdit(currentProject, now)

    await runTransaction(db, async (transaction) => {
      // すべての read を write より前に実行する（Firestore トランザクションの制約）
      const projectSnap = await transaction.get(projectDocRef)
      const formSnap = await transaction.get(formRef)

      const baseProject = projectSnap.exists()
        ? toProjectEntity(projectSnap.id, projectSnap.data() as ProjectDoc, projectSnap.ref.path)
        : currentProject
      baseProject.school_account_id = schoolAccountId

      const mergedProject = applyEdit(baseProject, now)
      entityForReturn = mergedProject

      const formPayload: Partial<CompanySurveyFormDoc> = {
        ...surveyService.buildProjectFormSyncPayload(mergedProject),
        ...schoolFormInfo,
        updated_at: timestamp as unknown as CompanySurveyFormDoc['updated_at'],
      }
      const mergedFormDocForSnapshot: Partial<CompanySurveyFormDoc> = formSnap.exists()
        ? { ...(formSnap.data() as CompanySurveyFormDoc), ...formPayload }
        : ({ ...formPayload, ...createBaseFields(false) } as Partial<CompanySurveyFormDoc>)
      const formSnapshot = toCompanySurveyFormSnapshotJson(
        mergedFormDocForSnapshot,
        COMPANY_SURVEY_FORM_ID_CURRENT,
      )
      const historyPayload = buildProjectHistoryPayload(
        mergedProject,
        updatedBy,
        updateInfo,
        timestamp,
        formSnapshot,
      )

      transaction.update(projectDocRef, { ...projectUpdate, updated_at: timestamp })
      transaction.set(newHistoryRef, historyPayload)
      if (formSnap.exists()) {
        transaction.update(formRef, formPayload as UpdateData<CompanySurveyFormDoc>)
      } else {
        transaction.set(formRef, { ...formPayload, ...createBaseFields(false) } as CompanySurveyFormDoc)
      }
    })

    return entityForReturn
  }

  /**
   * プロジェクト本体は変えず updated_at のみ更新し、履歴を1件追加する（school / admin 共通）。
   * 回答期限変更・フォーム側の学校情報同期など、フォームだけ更新したあと履歴に残す場合に使う。
   * フォームが存在する場合は、その時点の form スナップショットも履歴へ保存する。
   * ※ syncFormFromProject は呼ばない（フォーム側の更新と二重同期・上書きを避けるため）。
   */
  private async appendProjectHistoryOnly(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    updatedBy: ProjectHistoryUpdatedBy,
    updateInfo: string,
  ): Promise<ProjectEntity> {
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const updatedProjectEntity: ProjectEntity = {
      ...currentProject,
      updated_at: now,
    }

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const formRef = refs.companySurveyForm(schoolAccountId, projectId)
    const newHistoryRef = doc(refs.projectHistories(schoolAccountId, projectId))

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      const formSnap = await transaction.get(formRef)
      const formSnapshot = formSnap.exists()
        ? toCompanySurveyFormSnapshotJson(formSnap.data() as CompanySurveyFormDoc, COMPANY_SURVEY_FORM_ID_CURRENT)
        : null

      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        updatedBy,
        updateInfo,
        timestamp,
        formSnapshot,
      )

      transaction.update(projectDocRef, {
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return updatedProjectEntity
  }

  /**
   * プロジェクトのフィールドは変えず updated_at のみ更新し、履歴を1件追加する（school 用）。
   */
  async appendProjectHistoryOnlyAsSchool(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    schoolUser: { id: string; schoolName: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.appendProjectHistoryOnly(schoolAccountId, projectId, currentProject, {
      userType: 'school',
      userId: schoolUser.id,
      userName: schoolUser.schoolName,
      userPath: schoolUser.path,
    }, updateInfo)
  }

  /**
   * プロジェクトのフィールドは変えず updated_at のみ更新し、履歴を1件追加する（admin 用）。
   */
  async appendProjectHistoryOnlyAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    adminUser: { id: string; name: string; path: string },
    updateInfo: string,
  ): Promise<ProjectEntity> {
    return this.appendProjectHistoryOnly(schoolAccountId, projectId, currentProject, {
      userType: 'admin',
      userId: adminUser.id,
      userName: adminUser.name,
      userPath: adminUser.path,
    }, updateInfo)
  }

  /**
   * 学校が「データ登録中へ戻す」を実行：進行状況を draft_list に戻す（collection_closed 時のみ利用想定）
   */
  async revertToDraftListToAddRecipientsAsSchool(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    schoolUser: { id: string; schoolName: string; path: string },
  ): Promise<ProjectEntity> {
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const updatedProjectEntity: ProjectEntity = {
      ...currentProject,
      status: 'draft_list',
      updated_at: now,
    }

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
    const newHistoryRef = doc(historyCollectionRef)
    const historyPayload = buildProjectHistoryPayload(
      updatedProjectEntity,
      { userType: 'school', userId: schoolUser.id, userName: schoolUser.schoolName, userPath: schoolUser.path },
      'データ登録中へ戻すため、進行状況を「データ登録中」に戻しました',
      timestamp,
    )

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transaction.update(projectDocRef, {
        status: 'draft_list',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return updatedProjectEntity
  }

  /**
   * 管理者が「データ登録中へ戻す」を実行：進行状況を draft_list に戻し、直近フォーム紐付けを解除する（collection_closed 時のみ利用想定）
   */
  async revertToDraftListToAddRecipientsAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    adminUser: { id: string; name: string; path: string },
  ): Promise<ProjectEntity> {
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const updatedProjectEntity: ProjectEntity = {
      ...currentProject,
      status: 'draft_list',
      updated_at: now,
    }

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
    const newHistoryRef = doc(historyCollectionRef)
    const historyPayload = buildProjectHistoryPayload(
      updatedProjectEntity,
      { userType: 'admin', userId: adminUser.id, userName: adminUser.name, userPath: adminUser.path },
      'データ登録中へ戻すため、進行状況を「データ登録中」に戻しました',
      timestamp,
    )

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transaction.update(projectDocRef, {
        status: 'draft_list',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return updatedProjectEntity
  }

  /**
   * 管理者が「回答受付中もしくは回答受付終了に戻す」を実行。
   * 「データ登録中へ戻す」の取り消し用。project.status のみ復元し、form.closed は変更しない。
   * 前提: データ登録中〜データ確認中、回答フォーム存在、本郵送回追加事業所が0件、未対応データ生成リクエストが0件。
   */
  async restoreResponsePhaseFromDraftListAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    adminUser: { id: string; name: string; path: string },
  ): Promise<ProjectEntity> {
    const townCompaniesColRef = refs.schoolAccountTownCompanies(schoolAccountId, projectId)
    const requestsColRef = refs.addTownCompanyRequests(schoolAccountId, projectId)
    const [townCompaniesSnap, requestsSnap, form] = await Promise.all([
      getDocs(query(townCompaniesColRef, where('is_deleted', '==', false))),
      getDocs(query(requestsColRef, where('is_deleted', '==', false))),
      new CompanySurveyService().getForm(schoolAccountId, projectId),
    ])

    const townCompanies = townCompaniesSnap.docs.map((d) => {
      const data = d.data() as { send_request_number?: string | null }
      return { sendRequestNumber: data.send_request_number }
    })
    const requests = requestsSnap.docs.map((d) => {
      const data = d.data() as { status?: string | null; send_request_number?: string | null }
      return {
        status: data.status ?? '',
        sendRequestNumber: data.send_request_number,
      }
    })

    if (
      !canRestoreResponsePhaseFromDraftList({
        projectStatus: currentProject.status,
        hasSurveyForm: !!form,
        townCompanies,
        requests,
      })
    ) {
      if (
        currentProject.status !== 'draft_list' &&
        currentProject.status !== 'operator_processing' &&
        currentProject.status !== 'waiting_for_approval'
      ) {
        throw new Error('データ登録中〜データ確認中の案件のみ実行できます')
      }
      if (!form) {
        throw new Error('回答フォームが見つかりません。はがき郵送後の案件でのみ実行できます')
      }
      if (hasUnsentAddedTownCompanies(townCompanies)) {
        throw new Error('本郵送回で追加した事業所が残っています。全て削除してから実行してください')
      }
      if (hasUnhandledUnboundDataGenRequests(requests)) {
        throw new Error(
          '未対応のデータ生成リクエストがあります。完了またはキャンセルしてから実行してください',
        )
      }
      throw new Error('回答受付フェーズへ戻せません。前提条件を確認してください')
    }

    // 上記ガード通過後は form が存在する
    const targetStatus = resolveRestoreResponsePhaseTargetStatus(!!form?.closed)
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const updatedProjectEntity: ProjectEntity = {
      ...currentProject,
      status: targetStatus,
      updated_at: now,
    }

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
    const newHistoryRef = doc(historyCollectionRef)
    const targetLabel = getProjectStatusLabel(targetStatus)
    const historyPayload = buildProjectHistoryPayload(
      updatedProjectEntity,
      { userType: 'admin', userId: adminUser.id, userName: adminUser.name, userPath: adminUser.path },
      `誤操作対応のため、進行状況を「${targetLabel}」に戻しました`,
      timestamp,
    )

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transaction.update(projectDocRef, {
        status: targetStatus,
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return updatedProjectEntity
  }

  /**
   * 管理者が「はがき郵送取消」を実行（preparing_shipment のときのみ想定）。
   * 指定した郵送回で確定した事業所は削除せず、郵送状態のみ戻す（send_status: pending / send_request_number: クリア）。
   * 併せて、その郵送回の請求・送付リストを論理削除し、関連リクエストの郵送回をクリアした上で、進行状況を draft_list に戻す。
   * これにより、既存の事業所を残したまま一部修正・追加削除・再郵送ができる。
   */
  async cancelShipmentToDraftListAsAdmin(
    schoolAccountId: string,
    projectId: string,
    currentProject: ProjectEntity,
    requestNumber: string,
    adminUser: { id: string; name: string; path: string },
  ): Promise<ProjectEntity> {
    if (!requestNumber) {
      throw new Error('取り消す郵送回が特定できません')
    }

    const now = new Date()
    const timestamp = Timestamp.fromDate(now)

    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const townCompaniesColRef = refs.schoolAccountTownCompanies(schoolAccountId, projectId)
    const invoiceItemsColRef = refs.schoolAccountInvoiceItems(schoolAccountId, projectId)
    const sendListsColRef = refs.companySurveySendLists(schoolAccountId, projectId)
    const requestsColRef = refs.addTownCompanyRequests(schoolAccountId, projectId)

    // トランザクション外でクエリして対象ドキュメントを集める（単一フィールド等値のため複合インデックス不要）
    const [townCompaniesSnap, invoiceItemsSnap, sendListsSnap, requestsSnap] = await Promise.all([
      getDocs(townCompaniesColRef),
      getDocs(invoiceItemsColRef),
      getDocs(query(sendListsColRef, where('request_number', '==', requestNumber))),
      getDocs(query(requestsColRef, where('send_request_number', '==', requestNumber))),
    ])

    // 対象郵送回の事業所（論理削除されていないもの）。削除はせず郵送状態のみ戻す。
    const targetTownCompanyRefs = townCompaniesSnap.docs
      .filter((d) => {
        const data = d.data()
        return data.is_deleted !== true && data.send_request_number === requestNumber
      })
      .map((d) => d.ref)

    // 残存する郵送回の集合（旧データ＝郵送回未保持の請求を消してよいか判定するため）
    const remainingRounds = new Set(
      townCompaniesSnap.docs
        .filter((d) => d.data().is_deleted !== true)
        .map((d) => d.data().send_request_number)
        .filter((n): n is string => !!n),
    )
    const isSingleRound = remainingRounds.size <= 1

    // 対象郵送回のはがき請求明細（論理削除されていないもの）。
    // 旧データは send_request_number を持たないため、郵送回が1つだけの場合に限り postcard_* を対象にする。
    const targetInvoiceItemRefs = invoiceItemsSnap.docs
      .filter((d) => {
        const data = d.data()
        if (data.is_deleted === true) return false
        const isPostcard =
          data.item_type === 'postcard_postage_fee' || data.item_type === 'postcard_postage_discount'
        if (!isPostcard) return false
        if (data.send_request_number === requestNumber) return true
        return data.send_request_number == null && isSingleRound
      })
      .map((d) => d.ref)

    // 対象郵送回の送付リスト
    const targetSendListRefs = sendListsSnap.docs
      .filter((d) => d.data().is_deleted !== true)
      .map((d) => d.ref)

    // 対象郵送回が付与された追加データ生成リクエスト（郵送回をクリア）
    const targetRequestRefs = requestsSnap.docs
      .filter((d) => d.data().is_deleted !== true)
      .map((d) => d.ref)

    const updatedProjectEntity: ProjectEntity = {
      ...currentProject,
      status: 'draft_list',
      updated_at: now,
    }

    const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
    const newHistoryRef = doc(historyCollectionRef)
    const historyPayload = buildProjectHistoryPayload(
      updatedProjectEntity,
      { userType: 'admin', userId: adminUser.id, userName: adminUser.name, userPath: adminUser.path },
      'はがき郵送取消',
      timestamp,
    )

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }

      targetTownCompanyRefs.forEach((ref) => {
        transaction.update(ref, { send_status: 'pending', send_request_number: null, updated_at: timestamp })
      })
      targetInvoiceItemRefs.forEach((ref) => {
        transaction.update(ref, { is_deleted: true, updated_at: timestamp })
      })
      targetSendListRefs.forEach((ref) => {
        transaction.update(ref, { is_deleted: true, updated_at: timestamp })
      })
      targetRequestRefs.forEach((ref) => {
        transaction.update(ref, { send_request_number: null, updated_at: timestamp })
      })
      transaction.update(projectDocRef, {
        status: 'draft_list',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return updatedProjectEntity
  }

  /**
   * 学校が追加データ生成を依頼（リクエスト作成＋プロジェクトステータス更新＋履歴作成）
   * @returns 作成した AddTownCompanyRequest
   */
  async createAddTownCompanyRequestAsSchool(
    projectId: string,
    requestedCount: number,
    schoolAccount: SchoolAccount,
  ): Promise<AddTownCompanyRequest> {
    const projectDocRef = refs.schoolAccountProject(schoolAccount.id, projectId)
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)
    const requestsCollectionRef = refs.addTownCompanyRequests(schoolAccount.id, projectId)
    const newRequestRef = doc(requestsCollectionRef)

    const requestPayload: AddTownCompanyRequestDoc = {
      project_id: projectId,
      project_path: projectDocRef.path,
      requested_count: requestedCount,
      status: 'in_progress',
      is_billed: false,
      created_at: timestamp,
      updated_at: timestamp,
      is_deleted: false,
    }

    await runTransaction(db, async (transaction) => {
      const projectDocSnapshot = await transaction.get(projectDocRef)
      if (!projectDocSnapshot.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }

      const currentProject = toProjectEntity(
        projectDocSnapshot.id,
        projectDocSnapshot.data() as ProjectDoc,
        projectDocSnapshot.ref.path,
      )
      currentProject.school_account_id = schoolAccount.id

      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'operator_processing',
        updated_at: now,
      }

      const historyCollectionRef = refs.projectHistories(schoolAccount.id, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'school',
          userId: schoolAccount.id,
          userName: schoolAccount.schoolName,
          userPath: schoolAccount.path,
        },
        '追加データ生成を依頼しました',
        timestamp,
      )

      transaction.set(newRequestRef, requestPayload)
      transaction.update(projectDocRef, {
        status: 'operator_processing',
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
    })

    return toAddTownCompanyRequest(newRequestRef.id, requestPayload, newRequestRef.path)
  }

  async completeProjectAsAdmin(schoolId: string, projectId: string, adminAccount: AdminAccount): Promise<ProjectEntity> {
    const projectDocRef = refs.schoolAccountProject(schoolId, projectId)
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)

    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }
      const projectData = projectDocSnap.data() as ProjectDoc
      const formShortCode = (projectData as { form_short_code?: string | null }).form_short_code
      let shortUrlRef: ReturnType<typeof refs.formShortUrl> | null = null
      if (formShortCode) {
        shortUrlRef = refs.formShortUrl(formShortCode)
      }
      const shortUrlSnap = shortUrlRef ? await transaction.get(shortUrlRef) : null

      const currentProject = toProjectEntity(
        projectDocSnap.id,
        projectData,
        projectDocSnap.ref.path,
      )
      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'billing_completed',
        updated_at: now,
      }
      const historyCollectionRef = refs.projectHistories(schoolId, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'admin',
          userId: adminAccount.id,
          userName: adminAccount.nameKanji || '管理者',
          userPath: adminAccount.path,
        },
        '請求処理を完了しました',
        timestamp,
      )
      transaction.update(projectDocRef, {
        status: 'billing_completed',
        completed_at: timestamp,
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
      if (shortUrlRef && shortUrlSnap?.exists()) {
        const deleteAt = new Date(now)
        deleteAt.setMonth(deleteAt.getMonth() + 3)
        transaction.update(shortUrlRef, {
          scheduled_delete_at: Timestamp.fromDate(deleteAt),
        } as UpdateData<FormShortUrlDoc>)
      }
    })

    const updatedSnap = await getDoc(projectDocRef)
    const data = updatedSnap.exists() ? updatedSnap.data() : null
    if (!data) {
      throw new Error('プロジェクトの更新後にデータを取得できません')
    }
    return toProjectEntity(updatedSnap.id, data as ProjectDoc, projectDocRef.path)
  }

  /**
   * 学校が案件を完了する（案件完了: status=completed）。
   * 回答受付終了（collection_closed）のときのみ実行可能。
   */
  async completeProjectAsSchool(
    schoolAccountId: string,
    projectId: string,
    schoolUser: { id: string; schoolName: string; path: string },
  ): Promise<ProjectEntity> {
    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)

    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }
      const projectData = projectDocSnap.data() as ProjectDoc
      const currentStatus = projectData.status
      if (currentStatus !== 'collection_closed') {
        throw new Error('回答受付終了の状態でのみ案件を完了できます')
      }

      const formShortCode = (projectData as { form_short_code?: string | null }).form_short_code
      let shortUrlRef: ReturnType<typeof refs.formShortUrl> | null = null
      if (formShortCode) {
        shortUrlRef = refs.formShortUrl(formShortCode)
      }
      const shortUrlSnap = shortUrlRef ? await transaction.get(shortUrlRef) : null

      const currentProject = toProjectEntity(
        projectDocSnap.id,
        projectData,
        projectDocSnap.ref.path,
      )
      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'completed',
        updated_at: now,
      }
      const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'school',
          userId: schoolUser.id,
          userName: schoolUser.schoolName,
          userPath: schoolUser.path,
        },
        '案件を完了しました',
        timestamp,
      )
      transaction.update(projectDocRef, {
        status: 'completed',
        completed_at: timestamp,
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
      if (shortUrlRef && shortUrlSnap?.exists()) {
        const deleteAt = new Date(now)
        deleteAt.setMonth(deleteAt.getMonth() + 3)
        transaction.update(shortUrlRef, {
          scheduled_delete_at: Timestamp.fromDate(deleteAt),
        } as UpdateData<FormShortUrlDoc>)
      }
    })

    const updatedSnap = await getDoc(projectDocRef)
    const data = updatedSnap.exists() ? updatedSnap.data() : null
    if (!data) {
      throw new Error('プロジェクトの更新後にデータを取得できません')
    }
    const entity = toProjectEntity(updatedSnap.id, data as ProjectDoc, projectDocRef.path)
    entity.school_account_id = schoolAccountId
    return entity
  }

  /**
   * 管理者が学校に代わって案件を完了する（案件完了: status=completed）。
   * 回答受付終了（collection_closed）のときのみ実行可能。
   */
  async completeProjectAsAdminOnBehalfOfSchool(
    schoolAccountId: string,
    projectId: string,
    adminAccount: AdminAccount,
  ): Promise<ProjectEntity> {
    const projectDocRef = refs.schoolAccountProject(schoolAccountId, projectId)
    const db = getFirestore(getFirebaseApp())
    const now = new Date()
    const timestamp = Timestamp.fromDate(now)

    await runTransaction(db, async (transaction) => {
      const projectDocSnap = await transaction.get(projectDocRef)
      if (!projectDocSnap.exists()) {
        throw new Error('プロジェクトデータが見つかりません')
      }
      const projectData = projectDocSnap.data() as ProjectDoc
      if (projectData.status !== 'collection_closed') {
        throw new Error('回答受付終了の状態でのみ案件を完了できます')
      }

      const formShortCode = (projectData as { form_short_code?: string | null }).form_short_code
      let shortUrlRef: ReturnType<typeof refs.formShortUrl> | null = null
      if (formShortCode) {
        shortUrlRef = refs.formShortUrl(formShortCode)
      }
      const shortUrlSnap = shortUrlRef ? await transaction.get(shortUrlRef) : null

      const currentProject = toProjectEntity(
        projectDocSnap.id,
        projectData,
        projectDocSnap.ref.path,
      )
      const updatedProjectEntity: ProjectEntity = {
        ...currentProject,
        status: 'completed',
        updated_at: now,
      }
      const historyCollectionRef = refs.projectHistories(schoolAccountId, projectId)
      const newHistoryRef = doc(historyCollectionRef)
      const historyPayload = buildProjectHistoryPayload(
        updatedProjectEntity,
        {
          userType: 'admin',
          userId: adminAccount.id,
          userName: adminAccount.nameKanji || '管理者',
          userPath: adminAccount.path,
        },
        '管理者が学校に代わり案件を完了しました',
        timestamp,
      )
      transaction.update(projectDocRef, {
        status: 'completed',
        completed_at: timestamp,
        updated_at: timestamp,
      })
      transaction.set(newHistoryRef, historyPayload)
      if (shortUrlRef && shortUrlSnap?.exists()) {
        const deleteAt = new Date(now)
        deleteAt.setMonth(deleteAt.getMonth() + 3)
        transaction.update(shortUrlRef, {
          scheduled_delete_at: Timestamp.fromDate(deleteAt),
        } as UpdateData<FormShortUrlDoc>)
      }
    })

    const updatedSnap = await getDoc(projectDocRef)
    const data = updatedSnap.exists() ? updatedSnap.data() : null
    if (!data) {
      throw new Error('プロジェクトの更新後にデータを取得できません')
    }
    const entity = toProjectEntity(updatedSnap.id, data as ProjectDoc, projectDocRef.path)
    entity.school_account_id = schoolAccountId
    return entity
  }

  /**
   * 進行中案件一覧用の collection group クエリを返す。
   * 全学校横断で is_deleted==false かつ status が billing_completed 以外の案件を取得する。
   */
  static getActiveProjectsQuery(db: Firestore): Query<ProjectDoc> {
    const groupRef = collectionGroup(db, 'projects').withConverter(projectDocConverter)
    return query(
      groupRef,
      where('is_deleted', '==', false),
      where('status', '!=', 'billing_completed'),
      orderBy('status'),
      orderBy('created_at', 'desc'),
    )
  }

  /**
   * 指定月に請求確定した案件を一括取得（collection group クエリ）
   * @param start 対象月の開始日時（その月の 00:00:00）
   * @param end 対象月の終了日時（翌月1日 00:00:00）
   */
  async getCompletedProjectsInMonth(start: Date, end: Date): Promise<ProjectEntity[]> {
    const db = getFirestore(getFirebaseApp())
    const groupRef = collectionGroup(db, 'projects').withConverter(projectDocConverter)
    const startTs = Timestamp.fromDate(start)
    const endTs = Timestamp.fromDate(end)
    const q = query(
      groupRef,
      where('is_deleted', '==', false),
      where('status', '==', 'billing_completed'),
      where('completed_at', '>=', startTs),
      where('completed_at', '<', endTs),
      orderBy('completed_at', 'asc'),
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.map((d) => {
      const entity = toProjectEntity(d.id, d.data() as ProjectDoc, d.ref.path)
      const pathParts = d.ref.path.split('/')
      if (pathParts[0] === 'school_accounts' && pathParts.length >= 2) {
        entity.school_account_id = pathParts[1]
      }
      return entity
    })
  }

  /**
   * 請求確定かつ未連携の案件を一覧取得（collection group。請求対象の絞り込み用）
   * status=billing_completed かつ billed_at が未設定の案件を返す。
   */
  async getCompletedUnbilledProjects(): Promise<ProjectEntity[]> {
    const db = getFirestore(getFirebaseApp())
    const groupRef = collectionGroup(db, 'projects').withConverter(projectDocConverter)
    const q = query(
      groupRef,
      where('is_deleted', '==', false),
      where('status', '==', 'billing_completed'),
      orderBy('completed_at', 'asc'),
    )
    const snapshot = await getDocs(q)
    const entities = snapshot.docs.map((d) => {
      const entity = toProjectEntity(d.id, d.data() as ProjectDoc, d.ref.path)
      const pathParts = d.ref.path.split('/')
      if (pathParts[0] === 'school_accounts' && pathParts.length >= 2) {
        entity.school_account_id = pathParts[1]
      }
      return entity
    })
    return entities.filter((p) => !p.billed_at && !p.moneyforward_billing_id)
  }

  /**
   * 案件に請求済み情報を保存（マネーフォワード請求書作成後に呼ぶ）
   */
  async setProjectBilled(
    schoolAccountId: string,
    projectId: string,
    moneyforwardBillingId: string,
  ): Promise<void> {
    const projectRef = refs.project(schoolAccountId, projectId)
    await updateWithUpdatedAt(projectRef, {
      moneyforward_billing_id: moneyforwardBillingId,
      billed_at: Timestamp.fromDate(new Date()),
    } as UpdateData<ProjectDoc>)
  }
}